/**
 * The entity's own triples in the triples lens: Turtle, JSON-LD or N-Triples, to copy or save.
 * The facts' plain table is the property table, and the shape lens is the data model.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CodeBlock, Segmented } from '@bn-apps/dim-ui-react';
import type { PrefixMap } from '@utils/prefix.utils';
import type { EntityProperty } from '@/types';
import { serializeToJsonLd, serializeToNTriples, serializeToTurtle } from '@utils/rdfSerialize.utils';
import styles from '../EntityTemplate.module.scss';

type Format = 'turtle' | 'jsonld' | 'ntriples';

const EXTENSION: Record<Format, [string, string]> = {
  turtle: ['ttl', 'text/turtle'],
  jsonld: ['jsonld', 'application/ld+json'],
  ntriples: ['nt', 'application/n-triples'],
};

interface TriplesProps {
  uri: string;
  properties: EntityProperty[];
  prefixes: PrefixMap;
}

const Triples = ({ uri, properties, prefixes }: TriplesProps): JSX.Element => {
  const { t } = useTranslation();
  const [format, setFormat] = useState<Format>('turtle');
  const text =
    `${format === 'turtle' ? serializeToTurtle(uri, properties, prefixes) : format === 'jsonld' ? serializeToJsonLd(uri, properties, prefixes) : serializeToNTriples(uri, properties)}
`;
  const count = properties.reduce((sum, property) => sum + property.objects.length, 0);
  const download = () => {
    const [extension, type] = EXTENSION[format];
    const url = URL.createObjectURL(new Blob([text], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${uri.split(/[#/]/).pop() || 'entity'}.${extension}`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <section className={styles.section} aria-labelledby="entity-triples">
      <div className={styles.sectionHead}>
        <h2 id="entity-triples" className={styles.sectionTitle}>
          {t('entity.template.triples.title')} <span className={styles.titleCount}>{t('entity.template.triples.count', { count })}</span>
        </h2>
        <span className={styles.triplesActions}>
          <Segmented
            label={t('entity.template.triples.format')}
            hideLabel
            size="sm"
            value={format}
            onChange={setFormat}
            options={[
              { value: 'turtle', label: 'Turtle' },
              { value: 'jsonld', label: 'JSON-LD' },
              { value: 'ntriples', label: 'N-Triples' },
            ]}
          />
          <Button variant="secondary" size="sm" onClick={download}>
            {t('entity.template.triples.download')}
          </Button>
        </span>
      </div>
      <div className={styles.triplesBody}>
        <CodeBlock language={format === 'jsonld' ? 'json' : 'turtle'} copyText={text}>
          {text}
        </CodeBlock>
      </div>
    </section>
  );
};

export default Triples;
